import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileSpreadsheet, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";

// mode: "import" (add meters) | "status" (update statuses/colors by serial)
export default function ImportModal({ open, onOpenChange, mode, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const isStatus = mode === "status";
  const isComplete = mode === "complete";

  const reset = () => {
    setFile(null);
    setPreview([]);
    setLoading(false);
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        setPreview(rows.slice(0, 6));
      } catch {
        setPreview([]);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = isComplete ? "/meters/complete-import" : isStatus ? "/meters/status-import" : "/meters/import";
      const res = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isComplete) {
        toast.success(`${res.data.updated} passé(s) en Terminé${res.data.inserted ? `, ${res.data.inserted} ajouté(s)` : ""}`);
      } else if (isStatus) {
        toast.success(`${res.data.updated} statut(s) Excel mis à jour${res.data.not_found ? ` · ${res.data.not_found} non trouvé(s)` : ""}`);
      } else {
        toast.success(`${res.data.inserted} ajouté(s)${res.data.updated ? `, ${res.data.updated} mis à jour` : ""} · géocodage en cours…`);
      }
      reset();
      onOpenChange(false);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec du traitement du fichier");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl" data-testid="import-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {isComplete ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : isStatus ? <RefreshCw className="h-5 w-5 text-[#00A896]" /> : <UploadCloud className="h-5 w-5 text-[#00A896]" />}
            {isComplete ? "Importer les compteurs terminés (Excel)" : isStatus ? "Importer les statuts (Ordres de travail)" : "Ajouter des compteurs (Excel)"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isComplete
              ? "Importez l'export des compteurs remplacés/terminés. Chaque compteur est identifié par son numéro de série (ex. 551305). Les compteurs déjà présents passent en « Terminé » (vert) et ceux qui n'existent pas encore sont ajoutés directement en « Terminé ». Aucun compteur n'est supprimé."
              : isStatus
              ? "Importez le tableau des Ordres de travail. Chaque compteur est mis à jour selon la colonne Statut (À prendre en charge = jaune, Planifié non transmis = bleu, Transmis = cyan), identifié par son numéro de série. Aucun compteur n'est supprimé."
              : "Chaque ligne = un compteur. Les compteurs déjà présents (même numéro de série) sont mis à jour, les nouveaux sont ajoutés — jamais de doublon. Colonnes : lot, Numéro de série, Statut, Rue, No., Code postal, Lieu, Nom, Email, Téléphone, Notes, Catégorie."}
          </DialogDescription>
        </DialogHeader>

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center cursor-pointer hover:border-[#00A896]/60 transition-colors"
          data-testid="import-dropzone"
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="excel-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-slate-200">
              <FileSpreadsheet className="h-5 w-5 text-[#00A896]" />
              {file.name}
            </div>
          ) : (
            <div className="text-slate-400">
              <UploadCloud className="h-8 w-8 mx-auto mb-2 text-slate-500" />
              Glissez un fichier .xlsx ici ou cliquez pour parcourir
            </div>
          )}
        </div>

        {preview.length > 0 && (
          <div className="rounded-lg border border-slate-800 overflow-auto max-h-48">
            <table className="w-full text-xs">
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className={i === 0 ? "bg-slate-800/60 font-semibold" : "border-t border-slate-800"}>
                    {row.slice(0, 8).map((c, j) => (
                      <td key={j} className="px-2 py-1 whitespace-nowrap text-slate-300 max-w-[120px] truncate">
                        {String(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="bg-transparent border-slate-700">
            Annuler
          </Button>
          <Button onClick={submit} disabled={!file || loading} data-testid="import-confirm-button" className="bg-[#00A896] hover:bg-[#028090] text-[#04141a] font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isComplete ? "Passer en Terminé" : isStatus ? "Mettre à jour" : "Importer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
